import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

function Cart({ cart, addToCart, decreaseQuantity, removeFromCart, user, setCart }) {
  const navigate = useNavigate();
  const total = cart.reduce((acc, item) => acc + item.price * item.qty, 0);
  const cartCount = cart.reduce((acc, item) => acc + item.qty, 0);

  // Shipping Address Form State
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Post-checkout success view
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);

  const handleCheckoutClick = () => {
    if (!user) {
      navigate("/login");
    } else {
      setCheckoutOpen(true);
    }
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const token = localStorage.getItem("token");
    if (!token) {
      setError("Session expired. Please sign in again.");
      setLoading(false);
      return;
    }

    const orderItems = cart.map((item) => ({
      name: item.name,
      qty: item.qty,
      image: item.image,
      price: item.price,
      product: item._id,
    }));

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API}/api/orders`,
        {
          orderItems,
          shippingAddress: {
            address,
            city,
            postalCode,
            country,
          },
          totalPrice: total,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPlacedOrder(data);
      setOrderSuccess(true);
      setCart([]); // Clear the shopping cart
      setCheckoutOpen(false);
    } catch (err) {
      setError(
        err.response && err.response.data.message
          ? err.response.data.message
          : "Failed to place your order. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (orderSuccess && placedOrder) {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50 mb-6">
          <svg className="h-10 w-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-2">Order Confirmed!</h1>
        <p className="text-gray-500 mb-8 text-sm">
          Thank you for your order, {user?.name}. Your order ID is <code className="bg-gray-100 px-1 py-0.5 rounded text-xs text-gray-700 font-mono">{placedOrder._id}</code>.
        </p>

        <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 text-left mb-8 space-y-4">
          <h3 className="font-semibold text-gray-900 text-sm border-b border-gray-100 pb-3">Delivery Information</h3>
          <div className="text-xs text-gray-600 space-y-1.5">
            <p><span className="font-medium text-gray-800">Address:</span> {placedOrder.shippingAddress?.address}</p>
            <p><span className="font-medium text-gray-800">City/Postal:</span> {placedOrder.shippingAddress?.city}, {placedOrder.shippingAddress?.postalCode}</p>
            <p><span className="font-medium text-gray-800">Country:</span> {placedOrder.shippingAddress?.country}</p>
          </div>
          <div className="border-t border-gray-100 pt-3 flex justify-between items-center text-sm font-semibold text-gray-900">
            <span>Total Paid</span>
            <span>${placedOrder.totalPrice?.toLocaleString()}</span>
          </div>
        </div>

        <Link
          to="/"
          className="inline-block bg-black hover:bg-gray-800 text-white px-8 py-3 text-sm font-medium rounded-md transition-all shadow-sm"
        >
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-12 px-4 sm:px-0">
      <h1 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight">Shopping Cart</h1>
      
      {cart.length === 0 ? (
        <div className="border border-gray-200 p-16 text-center bg-gray-50 flex flex-col items-center rounded-xl">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-400">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Your cart is empty</h2>
          <p className="text-gray-500 mb-6 text-sm">Looks like you haven't added anything to your cart yet.</p>
          <Link to="/" className="bg-black text-white px-6 py-2.5 text-sm font-medium hover:bg-gray-800 transition-colors rounded-md shadow-sm">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-grow">
            <div className="border-t border-gray-100">
              {cart.map((item) => (
                <div key={item._id} className="py-6 border-b border-gray-100 flex items-center gap-6">
                  <div className="w-20 h-20 bg-white p-2 border border-gray-100 flex items-center justify-center shrink-0 rounded-lg shadow-sm">
                    <img src={item.image} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" />
                  </div>
                  <div className="flex-grow">
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5 font-bold">{item.category}</div>
                    <h3 className="font-semibold text-sm text-gray-900 leading-snug">{item.name}</h3>
                  </div>

                  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden h-8 bg-gray-50 shadow-sm shrink-0">
                    <button onClick={() => decreaseQuantity(item._id)} className="w-8 h-full flex items-center justify-center hover:bg-gray-100 font-bold text-gray-500 transition-colors">-</button>
                    <span className="w-8 text-center font-medium text-xs text-gray-900 select-none bg-white h-full flex items-center justify-center">{item.qty}</span>
                    <button onClick={() => addToCart(item)} className="w-8 h-full flex items-center justify-center hover:bg-gray-100 font-bold text-gray-500 transition-colors">+</button>
                  </div>

                  <div className="font-bold text-sm text-gray-900 w-24 text-right tracking-tight shrink-0">
                    ${(item.price * item.qty).toLocaleString()}
                  </div>
                  
                  <button
                    onClick={() => removeFromCart(item._id)}
                    className="text-gray-300 hover:text-red-500 p-2 transition-colors ml-2 shrink-0"
                    title="Remove item"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="w-full lg:w-80 shrink-0">
            <div className="bg-white border border-gray-100 rounded-xl p-6 sticky top-24 shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Order Summary</h3>
              <div className="space-y-3 mb-6 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>Subtotal ({cartCount} items)</span>
                  <span className="font-medium text-gray-900">${total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Shipping</span>
                  <span className="text-gray-400 font-medium text-[10px] tracking-wide uppercase mt-0.5">Free</span>
                </div>
                <div className="border-t border-gray-100 pt-3 mt-3 flex justify-between items-center">
                  <span className="font-bold text-gray-900">Total</span>
                  <span className="font-bold text-lg text-gray-900 tracking-tight">${total.toLocaleString()}</span>
                </div>
              </div>
              
              <button
                onClick={handleCheckoutClick}
                className="w-full bg-black hover:bg-gray-800 text-white py-3 font-semibold text-sm transition-all rounded-lg shadow-sm"
              >
                {!user ? "Sign In to Checkout" : "Checkout Securely"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Shipping Modal */}
      {checkoutOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm" onClick={() => setCheckoutOpen(false)}></div>
          
          {/* Modal Container */}
          <div className="relative w-full max-w-md mx-auto my-6 px-4 z-50">
            <div className="relative flex flex-col w-full bg-white border border-gray-100 rounded-2xl shadow-2xl outline-none focus:outline-none overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              {/* Header */}
              <div className="flex items-start justify-between p-5 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-950">Shipping Details</h3>
                <button
                  className="p-1 ml-auto bg-transparent border-0 text-gray-400 hover:text-black float-right text-3xl leading-none font-semibold outline-none focus:outline-none transition-colors"
                  onClick={() => setCheckoutOpen(false)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form Body */}
              <form onSubmit={handlePlaceOrder}>
                <div className="relative p-6 flex-auto space-y-4">
                  {error && (
                    <div className="rounded-lg bg-red-50 p-3 border border-red-100 text-xs text-red-600 flex items-start">
                      <svg className="mr-2 h-4 w-4 shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span>{error}</span>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="123 Main St, Apt 4B"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black transition-all"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="New York"
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black transition-all"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                        Postal Code
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="10001"
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black transition-all"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="United States"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black transition-all"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                    />
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-end p-5 border-t border-gray-100 gap-3">
                  <button
                    type="button"
                    className="text-gray-500 hover:text-black text-sm font-semibold px-4 py-2 transition-colors"
                    onClick={() => setCheckoutOpen(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-black hover:bg-gray-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 flex items-center"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Placing Order...
                      </>
                    ) : (
                      "Place Order"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cart;
